import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Eye, EyeOff, Mail, Lock, ArrowRight, CheckCircle, AlertCircle, User, Building, X } from 'lucide-react';
import { GoogleLogin } from '@react-oauth/google';
import { useTheme } from '../context/ThemeContext';

const RECAPTCHA_SITE_KEY = '6LcbB5EsAAAAAOISef2aJsbOaPKBWNY1Q_q2RwUb';

const MarsSignupForm = () => {
  const { isDark } = useTheme();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    businessEmail: '',
    company: '',
    password: '',
    acceptTerms: false
  });
  const [recaptchaToken, setRecaptchaToken] = useState('');
  const recaptchaRef = useRef(null);
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [signupSuccess, setSignupSuccess] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [etherealLink, setEtherealLink] = useState('');
  const [focusedField, setFocusedField] = useState('');
  const [errors, setErrors] = useState({});
  const [isFormValid, setIsFormValid] = useState(false);
  const [floatingElements, setFloatingElements] = useState([]);
  const [formProgress, setFormProgress] = useState(0);
  const [submitError, setSubmitError] = useState('');
  
  // Modal states
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);

  // Load Tawk.to chat widget for all visitors on signup page
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') return;
    const existingScript = document.querySelector('script[src*="tawk.to"]');
    if (existingScript) return;

    window.Tawk_API = window.Tawk_API || {};
    window.Tawk_LoadStart = new Date();
    window.Tawk_API.onLoad = () => {
      if (window.Tawk_API && window.Tawk_API.minimize) {
        window.Tawk_API.minimize();
      }
    };

    const script = document.createElement('script');
    script.async = true;
    script.src = 'https://embed.tawk.to/67f52af29b5f671906ebc6a3/1ioaqvhsq';
    script.charset = 'UTF-8';
    script.setAttribute('crossorigin', '*');
    const firstScript = document.getElementsByTagName('script')[0];
    if (firstScript && firstScript.parentNode) {
      firstScript.parentNode.insertBefore(script, firstScript);
    }
  }, []);

  // Load Google reCAPTCHA v2 script
  useEffect(() => {
    if (document.querySelector('script[src*="recaptcha"]')) return;
    const script = document.createElement('script');
    script.src = 'https://www.google.com/recaptcha/api.js?onload=onRecaptchaLoad&render=explicit';
    script.async = true;
    script.defer = true;
    document.head.appendChild(script);

    window.onRecaptchaLoad = () => {
      if (recaptchaRef.current && window.grecaptcha) {
        window.grecaptcha.render(recaptchaRef.current, {
          sitekey: RECAPTCHA_SITE_KEY,
          callback: (token) => setRecaptchaToken(token),
          'expired-callback': () => setRecaptchaToken(''),
          theme: isDark ? 'dark' : 'light'
        });
      }
    };

    return () => { window.onRecaptchaLoad = null; };
  }, []);

  // Render reCAPTCHA when script is already loaded (e.g. after re-mount)
  useEffect(() => {
    if (window.grecaptcha && window.grecaptcha.render && recaptchaRef.current) {
      try {
        window.grecaptcha.render(recaptchaRef.current, {
          sitekey: RECAPTCHA_SITE_KEY,
          callback: (token) => setRecaptchaToken(token),
          'expired-callback': () => setRecaptchaToken(''),
          theme: isDark ? 'dark' : 'light'
        });
      } catch (e) {
        // Already rendered
      }
    }
  }, [isDark]);

  // Generate floating background elements
  useEffect(() => {
    const elements = Array.from({ length: 6 }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      size: Math.random() * 60 + 20,
      delay: Math.random() * 2
    }));
    setFloatingElements(elements);
  }, []);

  // Helper function to validate email (no consecutive dots)
  const isEmailValid = (email) => {
    if (!email) return false;
    const trimmed = email.trim();
    if (/\.{2,}/.test(trimmed)) return false; // block consecutive dots
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed);
  };

  // Helper function to validate password completely
  const isPasswordValid = (password) => {
    return password && 
           password.length >= 8 && 
           /(?=.*[a-z])/.test(password) && 
           /(?=.*[A-Z])/.test(password) && 
           /(?=.*\d)/.test(password) && 
           /(?=.*[!@#$%^&*(),.?":{}|<>])/.test(password);
  };

  // Helper to check if a field value passes all validation rules
  const isFieldValueValid = (field, value) => {
    if (!value || !value.trim()) return false;
    switch (field) {
      case 'firstName':
      case 'lastName':
        return /^[a-zA-Z\s]+$/.test(value.trim()) && value.trim().length >= 2;
      case 'businessEmail':
        return isEmailValid(value);
      case 'company':
        return /^[a-zA-Z0-9\s.\-&]+$/.test(value.trim()) && value.trim().length >= 2;
      case 'password':
        return isPasswordValid(value);
      default:
        return value.trim().length > 0;
    }
  };

  // Calculate form progress and validate form
  useEffect(() => {
    const fields = ['firstName', 'lastName', 'businessEmail', 'company', 'password'];
    const filledFields = fields.filter(field => isFieldValueValid(field, formData[field]));

    const checkboxesValid = formData.acceptTerms && !!recaptchaToken;
    const progress = (filledFields.length / fields.length) * 80 + (checkboxesValid ? 20 : 0);

    setFormProgress(progress);
    setIsFormValid(progress === 100);
  }, [formData, recaptchaToken]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    // Block HTML tags and script injection on all text fields
    if (type !== 'checkbox' && /<[^>]*>/g.test(value)) {
      return;
    }

    // Only allow letters and spaces for first name and last name
    if (name === 'firstName' || name === 'lastName') {
      if (value && !/^[a-zA-Z\s]*$/.test(value)) {
        return;
      }
    }

    // Only allow letters, numbers, spaces, dots, hyphens, and ampersands for company
    if (name === 'company') {
      if (value && !/^[a-zA-Z0-9\s.\-&]*$/.test(value)) {
        return;
      }
    }

    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));

    // Validate field inline as user types
    if (type !== 'checkbox' && value) {
      const error = validateField(name, value);
      setErrors(prev => ({
        ...prev,
        [name]: error
      }));
    } else if (type !== 'checkbox') {
      // Clear error if field is emptied
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }

    // Clear submit error when user makes changes
    if (submitError) {
      setSubmitError('');
    }
  };

  const validateField = (name, value) => {
    let error = '';
    switch (name) {
      case 'firstName':
        if (!value || !value.trim()) error = 'First name is required';
        else if (!/^[a-zA-Z\s]+$/.test(value.trim())) error = 'Only letters are allowed';
        else if (value.trim().length < 2) error = 'First name must be at least 2 characters';
        break;
      case 'lastName':
        if (!value || !value.trim()) error = 'Last name is required';
        else if (!/^[a-zA-Z\s]+$/.test(value.trim())) error = 'Only letters are allowed';
        else if (value.trim().length < 2) error = 'Last name must be at least 2 characters';
        break;
      case 'businessEmail':
        if (!value || !value.trim()) error = 'Business email is required';
        else if (/\.{2,}/.test(value.trim())) error = 'Email cannot contain consecutive dots';
        else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())) error = 'Invalid email format';
        break;
      case 'company':
        if (!value || !value.trim()) error = 'Company name is required';
        else if (!/^[a-zA-Z0-9\s.\-&]+$/.test(value.trim())) error = 'Only letters, numbers, spaces, dots, hyphens and & are allowed';
        else if (value.trim().length < 2) error = 'Company name must be at least 2 characters';
        break;
      case 'password':
        if (!value) error = 'Password is required';
        else if (value.length < 8) error = 'Password must be at least 8 characters';
        else if (!/(?=.*[a-z])/.test(value)) error = 'Password must contain a lowercase letter';
        else if (!/(?=.*[A-Z])/.test(value)) error = 'Password must contain an uppercase letter';
        else if (!/(?=.*\d)/.test(value)) error = 'Password must contain a number';
        else if (!/(?=.*[!@#$%^&*(),.?":{}|<>])/.test(value)) error = 'Password must contain a special character';
        break;
      default:
        break;
    }
    return error;
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    const error = validateField(name, value);
    if (error) {
      setErrors(prev => ({
        ...prev,
        [name]: error
      }));
    }
    setFocusedField('');
  };

  const submitToDatabase = async (userData) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || 'https://mars2.mediacle.net'}/api/createuser`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          firstName: userData.firstName.trim(),
          lastName: userData.lastName.trim(),
          businessEmail: userData.businessEmail.toLowerCase().trim(),
          company: userData.company.trim(),
          password: userData.password,
          recaptchaToken: recaptchaToken,
        }),
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.message || 'Failed to create account');
      }

      return result;
    } catch (error) {
      console.error('Database submission error:', error);
      throw error;
    }
  };

  // Google OAuth signup — creates account, auto-verifies, redirects to pricing
  const handleGoogleSuccess = async (credentialResponse) => {
    setIsLoading(true);
    setSubmitError('');

    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'https://mars2.mediacle.net';
      const response = await fetch(`${apiUrl}/api/auth/google`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ credential: credentialResponse.credential })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        localStorage.setItem('user', JSON.stringify(data.user));
        localStorage.setItem('isAuthenticated', 'true');
        window.location.href = data.redirectTo || '/pricing-plan';
      } else {
        setSubmitError(data.message || 'Google signup failed. Please try again.');
      }
    } catch (err) {
      console.error('Google signup error:', err);
      setSubmitError('Network error during Google signup. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async () => {
    // Clear previous submit errors
    setSubmitError('');
    
    // Validate all fields
    const newErrors = {};
    const fieldsToValidate = ['firstName', 'lastName', 'businessEmail', 'company', 'password'];
    
    fieldsToValidate.forEach(key => {
      const error = validateField(key, formData[key]);
      if (error) {
        newErrors[key] = error;
      }
    });

    if (!formData.acceptTerms) {
      newErrors.acceptTerms = 'You must accept the terms and privacy policy';
    }

    if (!recaptchaToken) {
      newErrors.recaptcha = 'Please complete the reCAPTCHA verification';
    }

    // Check if there are any errors
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    // Double check all required fields are filled
    const requiredFields = ['firstName', 'lastName', 'businessEmail', 'company', 'password'];
    const missingFields = requiredFields.filter(field => !formData[field] || !formData[field].trim());
    
    if (missingFields.length > 0) {
      setSubmitError(`Please fill in all required fields: ${missingFields.join(', ')}`);
      return;
    }

    if (!formData.acceptTerms || !recaptchaToken) {
      setSubmitError('Please accept the terms and complete the reCAPTCHA');
      return;
    }

    setIsLoading(true);
    
    try {
      // Submit to database
      const result = await submitToDatabase(formData);
      
      
      // Store user email and Ethereal link for verification message
      setUserEmail(formData.businessEmail);
      if (result.etherealPreviewUrl) {
        setEtherealLink(result.etherealPreviewUrl);
      }
      setSignupSuccess(true);

      // Fire LinkedIn Insight conversion tag
      if (window.lintrk) {
        window.lintrk('track', { conversion_id: 27286833 });
      }

    } catch (error) {
      console.error('Signup failed:', error);
      setSubmitError(error.message || 'Failed to create account. Please try again.');
      // Reset reCAPTCHA on failure so user must re-verify
      setRecaptchaToken('');
      if (window.grecaptcha) {
        window.grecaptcha.reset();
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleBackToSignup = () => {
    setSignupSuccess(false);
    setUserEmail('');
    setEtherealLink('');
    setFormData({
      firstName: '',
      lastName: '',
      businessEmail: '',
      company: '',
      password: '',
      acceptTerms: false
    });
    setRecaptchaToken('');
    if (window.grecaptcha) {
      window.grecaptcha.reset();
    }
    setErrors({});
  };

  const getInputClassName = (fieldName) => {
    const baseClass = "w-full pl-10 pr-10 py-3 border-2 rounded-lg transition-all duration-300 text-sm text-gray-700 dark:text-slate-200 placeholder-gray-400 dark:placeholder-slate-500 bg-white/90 dark:bg-slate-800/90 backdrop-blur-sm";

    if (errors[fieldName]) {
      return `${baseClass} border-red-400 dark:border-red-500 focus:border-red-500 focus:ring-2 focus:ring-red-100 dark:focus:ring-red-900/30`;
    } else if (focusedField === fieldName) {
      return `${baseClass} border-teal-400 dark:border-teal-500 focus:border-teal-500 focus:ring-2 focus:ring-teal-100 dark:focus:ring-teal-900/30`;
    } else if (formData[fieldName] && isFieldValid(fieldName)) {
      return `${baseClass} border-green-400 dark:border-green-500 focus:border-teal-500 focus:ring-2 focus:ring-teal-100 dark:focus:ring-teal-900/30`;
    }
    return `${baseClass} border-gray-300 dark:border-slate-600 hover:border-gray-400 dark:hover:border-slate-500 focus:border-teal-400 focus:ring-2 focus:ring-teal-50 dark:focus:ring-teal-900/30`;
  };

  const getFieldIcon = (fieldName) => {
    const iconClass = `w-4 h-4 transition-all duration-300 ${
      focusedField === fieldName ? 'text-teal-600 dark:text-teal-400' :
      errors[fieldName] ? 'text-red-600 dark:text-red-400' :
      (formData[fieldName] && isFieldValid(fieldName)) ? 'text-green-600 dark:text-green-400' : 'text-gray-400 dark:text-slate-500'
    }`;

    const icons = {
      firstName: <User className={iconClass} />,
      lastName: <User className={iconClass} />,
      businessEmail: <Mail className={iconClass} />,
      company: <Building className={iconClass} />,
      password: <Lock className={iconClass} />
    };

    return icons[fieldName];
  };

  const isFieldValid = (fieldName) => {
    return formData[fieldName] && !errors[fieldName] && isFieldValueValid(fieldName, formData[fieldName]);
  };

  // Modal Component - FIXED VERSION
  const Modal = ({ isOpen, onClose, title, children }) => {
    useEffect(() => {
      if (isOpen) {
        document.body.style.overflow = 'hidden';
      } else {
        document.body.style.overflow = 'unset';
      }
      
      return () => {
        document.body.style.overflow = 'unset';
      };
    }, [isOpen]);

    if (!isOpen) return null;

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        {/* Backdrop */}
        <div
          className="absolute inset-0 bg-black/40 dark:bg-black/60 backdrop-blur-sm transition-opacity duration-300"
          onClick={onClose}
        />

        {/* Modal - Fixed container */}
        <div className="relative bg-white/95 dark:bg-slate-900/95 backdrop-blur-xl rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] flex flex-col animate-in fade-in zoom-in-95 duration-300 border border-gray-100 dark:border-slate-700">
          {/* Header - Fixed at top */}
          <div className="flex items-center justify-between p-6 border-b border-gray-100 dark:border-slate-700 flex-shrink-0">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-slate-100">{title}</h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 dark:hover:bg-slate-800 rounded-lg transition-colors duration-200 flex-shrink-0"
            >
              <X className="w-5 h-5 text-gray-500 dark:text-slate-400" />
            </button>
          </div>

          {/* Content - Scrollable */}
          <div className="flex-1 overflow-y-auto p-6 text-sm text-gray-700 dark:text-slate-300">
            {children}
          </div>

          {/* Footer - Fixed at bottom */}
          <div className="flex items-center justify-end p-6 border-t border-gray-100 dark:border-slate-700 bg-gray-50/50 dark:bg-slate-800/50 flex-shrink-0">
            <button
              onClick={onClose}
              className="px-6 py-2 bg-teal-600 hover:bg-teal-700 text-white text-sm font-medium rounded-lg transition-colors duration-200"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Privacy Policy Content
  const PrivacyPolicyContent = () => (
    <div className="space-y-4">
      <div>
        <p className="text-gray-500 dark:text-slate-400 text-xs mb-3">Last Updated: 27th March 2026</p>
        <p className="text-gray-700 dark:text-slate-300 text-sm">This Privacy Policy explains how Mediacle Limited ("Company", "we", "us", "our") collects, uses, processes, and protects personal data when you use the MARS&#8482; platform ("MARS", "Service").</p>
        <p className="text-gray-700 dark:text-slate-300 text-sm mt-2">We are committed to protecting your privacy and complying with applicable data protection laws, including the UK GDPR and EU GDPR where applicable.</p>
      </div>

      <div>
        <h3 className="font-semibold text-gray-900 dark:text-slate-100 mb-2">1. Who We Are</h3>
        <div className="text-gray-700 dark:text-slate-300 text-sm space-y-1">
          <p><strong>Data Controller:</strong> Mediacle Limited</p>
          <p><strong>Contact Email:</strong> mars@mediacle.com</p>
        </div>
        <p className="text-gray-700 dark:text-slate-300 text-sm mt-2">We act as:</p>
        <div className="text-gray-700 dark:text-slate-300 text-sm space-y-1 ml-3">
          <p>&#8226; Data Controller for account and usage data</p>
          <p>&#8226; Data Processor for client data processed through the platform</p>
        </div>
      </div>

      <div>
        <h3 className="font-semibold text-gray-900 dark:text-slate-100 mb-2">2. Information We Collect</h3>
        <div className="space-y-3">
          <div>
            <h4 className="font-medium text-gray-800 dark:text-slate-200 mb-1 text-sm">A. Information You Provide</h4>
            <div className="text-gray-700 dark:text-slate-300 text-sm space-y-1 ml-3">
              <p>&#8226; Name, email, company name, job title</p>
              <p>&#8226; Account credentials</p>
              <p>&#8226; Billing and payment details</p>
              <p>&#8226; Communication with us</p>
            </div>
          </div>
          <div>
            <h4 className="font-medium text-gray-800 dark:text-slate-200 mb-1 text-sm">B. Automatically Collected Data</h4>
            <div className="text-gray-700 dark:text-slate-300 text-sm space-y-1 ml-3">
              <p>&#8226; IP address</p>
              <p>&#8226; Device and browser information</p>
              <p>&#8226; Log data and usage behaviour</p>
              <p>&#8226; Cookies and tracking data</p>
            </div>
          </div>
          <div>
            <h4 className="font-medium text-gray-800 dark:text-slate-200 mb-1 text-sm">C. Client Data (Processed on Your Behalf)</h4>
            <div className="text-gray-700 dark:text-slate-300 text-sm space-y-1 ml-3">
              <p>&#8226; Affiliate data</p>
              <p>&#8226; Campaign and performance data</p>
              <p>&#8226; Other business-related data uploaded or processed via MARS</p>
            </div>
            <p className="text-gray-700 dark:text-slate-300 text-sm mt-1">We process this data strictly as a processor, on your instructions.</p>
          </div>
        </div>
      </div>

      <div>
        <h3 className="font-semibold text-gray-900 dark:text-slate-100 mb-2">3. How We Use Your Data</h3>
        <p className="text-gray-700 dark:text-slate-300 text-sm mb-1">We use personal data to:</p>
        <div className="text-gray-700 dark:text-slate-300 text-sm space-y-1 ml-3">
          <p>&#8226; Provide and operate the MARS&#8482; platform</p>
          <p>&#8226; Manage accounts and customer relationships</p>
          <p>&#8226; Process payments and billing</p>
          <p>&#8226; Improve product performance and user experience</p>
          <p>&#8226; Provide support and communications</p>
          <p>&#8226; Ensure platform security and prevent fraud</p>
        </div>
      </div>

      <div>
        <h3 className="font-semibold text-gray-900 dark:text-slate-100 mb-2">4. AI & Data Processing</h3>
        <p className="text-gray-700 dark:text-slate-300 text-sm mb-1">MARS&#8482; uses AI and automation technologies. We may:</p>
        <div className="text-gray-700 dark:text-slate-300 text-sm space-y-1 ml-3">
          <p>&#8226; Analyse data to improve platform performance</p>
          <p>&#8226; Use aggregated and anonymised data for product development</p>
        </div>
        <p className="text-gray-700 dark:text-slate-300 text-sm mt-2">We do NOT:</p>
        <div className="text-gray-700 dark:text-slate-300 text-sm space-y-1 ml-3">
          <p>&#8226; Use identifiable client data for external purposes</p>
          <p>&#8226; Sell personal data to third parties</p>
        </div>
      </div>

      <div>
        <h3 className="font-semibold text-gray-900 dark:text-slate-100 mb-2">5. Legal Basis for Processing</h3>
        <p className="text-gray-700 dark:text-slate-300 text-sm mb-1">We process personal data under the following legal bases:</p>
        <div className="text-gray-700 dark:text-slate-300 text-sm space-y-1 ml-3">
          <p>&#8226; <strong>Contractual necessity</strong> - to provide the Service</p>
          <p>&#8226; <strong>Legitimate interests</strong> - to improve and secure the platform</p>
          <p>&#8226; <strong>Legal obligations</strong> - where required by law</p>
          <p>&#8226; <strong>Consent</strong> - where applicable (e.g., marketing communications)</p>
        </div>
      </div>

      <div>
        <h3 className="font-semibold text-gray-900 dark:text-slate-100 mb-2">6. Data Sharing</h3>
        <p className="text-gray-700 dark:text-slate-300 text-sm mb-1">We may share data with:</p>
        <div className="text-gray-700 dark:text-slate-300 text-sm space-y-1 ml-3">
          <p>&#8226; Service providers (hosting, analytics, payment processors)</p>
          <p>&#8226; Professional advisors (legal, accounting)</p>
          <p>&#8226; Authorities where legally required</p>
        </div>
        <p className="text-gray-700 dark:text-slate-300 text-sm mt-2">All third parties are required to maintain confidentiality and data protection standards.</p>
      </div>

      <div>
        <h3 className="font-semibold text-gray-900 dark:text-slate-100 mb-2">7. International Data Transfers</h3>
        <p className="text-gray-700 dark:text-slate-300 text-sm mb-1">Your data may be transferred to and processed in countries outside the UK/EU. Where this occurs, we ensure appropriate safeguards, including:</p>
        <div className="text-gray-700 dark:text-slate-300 text-sm space-y-1 ml-3">
          <p>&#8226; Standard Contractual Clauses (SCCs)</p>
          <p>&#8226; Equivalent data protection measures</p>
        </div>
      </div>

      <div>
        <h3 className="font-semibold text-gray-900 dark:text-slate-100 mb-2">8. Data Retention</h3>
        <p className="text-gray-700 dark:text-slate-300 text-sm mb-1">We retain personal data only as long as necessary to:</p>
        <div className="text-gray-700 dark:text-slate-300 text-sm space-y-1 ml-3">
          <p>&#8226; Provide the Service</p>
          <p>&#8226; Comply with legal obligations</p>
          <p>&#8226; Resolve disputes</p>
        </div>
        <p className="text-gray-700 dark:text-slate-300 text-sm mt-2">Client data is retained as per contractual terms or deleted upon request, subject to legal requirements.</p>
      </div>

      <div>
        <h3 className="font-semibold text-gray-900 dark:text-slate-100 mb-2">9. Your Rights</h3>
        <p className="text-gray-700 dark:text-slate-300 text-sm mb-1">Under applicable data protection laws, you have the right to:</p>
        <div className="text-gray-700 dark:text-slate-300 text-sm space-y-1 ml-3">
          <p>&#8226; Access your data</p>
          <p>&#8226; Correct inaccurate data</p>
          <p>&#8226; Request deletion</p>
          <p>&#8226; Restrict or object to processing</p>
          <p>&#8226; Data portability</p>
          <p>&#8226; Withdraw consent (where applicable)</p>
        </div>
        <p className="text-gray-700 dark:text-slate-300 text-sm mt-2">To exercise your rights, contact: mars@mediacle.com</p>
      </div>

      <div>
        <h3 className="font-semibold text-gray-900 dark:text-slate-100 mb-2">10. Security</h3>
        <p className="text-gray-700 dark:text-slate-300 text-sm mb-1">We implement appropriate technical and organisational measures to protect data, including:</p>
        <div className="text-gray-700 dark:text-slate-300 text-sm space-y-1 ml-3">
          <p>&#8226; Secure infrastructure</p>
          <p>&#8226; Access controls</p>
          <p>&#8226; Encryption where applicable</p>
        </div>
        <p className="text-gray-700 dark:text-slate-300 text-sm mt-2">However, no system is completely secure.</p>
      </div>

      <div>
        <h3 className="font-semibold text-gray-900 dark:text-slate-100 mb-2">11. Cookies</h3>
        <p className="text-gray-700 dark:text-slate-300 text-sm mb-1">We use cookies and similar technologies to:</p>
        <div className="text-gray-700 dark:text-slate-300 text-sm space-y-1 ml-3">
          <p>&#8226; Enhance user experience</p>
          <p>&#8226; Analyse usage</p>
          <p>&#8226; Improve performance</p>
        </div>
        <p className="text-gray-700 dark:text-slate-300 text-sm mt-2">You can manage cookie preferences through your browser settings.</p>
      </div>

      <div>
        <h3 className="font-semibold text-gray-900 dark:text-slate-100 mb-2">12. Third-Party Links</h3>
        <p className="text-gray-700 dark:text-slate-300 text-sm">The Service may contain links to third-party websites. We are not responsible for their content or their privacy practices.</p>
      </div>

      <div>
        <h3 className="font-semibold text-gray-900 dark:text-slate-100 mb-2">13. Children's Data</h3>
        <p className="text-gray-700 dark:text-slate-300 text-sm">MARS&#8482; is not intended for individuals under 18. We do not knowingly collect personal data from children.</p>
      </div>

      <div>
        <h3 className="font-semibold text-gray-900 dark:text-slate-100 mb-2">14. Updates to This Policy</h3>
        <p className="text-gray-700 dark:text-slate-300 text-sm">We may update this Privacy Policy from time to time. Changes will be posted on this page with an updated "Last Updated" date.</p>
      </div>

      <div>
        <h3 className="font-semibold text-gray-900 dark:text-slate-100 mb-2">15. Contact</h3>
        <p className="text-gray-700 dark:text-slate-300 text-sm mb-2">For any privacy-related questions or requests:</p>
        <div className="bg-gray-50 dark:bg-slate-800 p-3 rounded text-sm">
          <p className="text-gray-700 dark:text-slate-300"><strong>Email:</strong> mars@mediacle.com</p>
          <p className="text-gray-700 dark:text-slate-300"><strong>Company:</strong> Mediacle Limited</p>
        </div>
      </div>
    </div>
  );

  // Terms of Service Content
  const TermsOfServiceContent = () => (
    <div className="space-y-4">
      <div>
        <p className="text-gray-500 dark:text-slate-400 text-xs mb-3">Last Updated: 27 March 2026</p>
        <p className="text-gray-700 dark:text-slate-300 text-sm">These Terms of Service ("Terms") govern your access to and use of the MARS&#8482; platform ("MARS", "Service") operated by Mediacle Limited ("Company", "we", "us", or "our").</p>
        <p className="text-gray-700 dark:text-slate-300 text-sm mt-2">By accessing or using MARS&#8482;, you agree to be bound by these Terms.</p>
      </div>

      <div>
        <h3 className="font-semibold text-gray-900 dark:text-slate-100 mb-2">1. Nature of Service</h3>
        <p className="text-gray-700 dark:text-slate-300 text-sm mb-2">MARS&#8482; is an AI-powered software platform designed to assist iGaming operators in discovering, recruiting, and managing affiliate partnerships.</p>
        <p className="text-gray-700 dark:text-slate-300 text-sm mb-1">The Service provides:</p>
        <div className="text-gray-700 dark:text-slate-300 text-sm space-y-1 ml-3">
          <p>&#8226; Data insights and affiliate discovery tools</p>
          <p>&#8226; Automation tools for outreach and recruitment</p>
          <p>&#8226; Performance tracking and analytics</p>
        </div>
        <p className="text-gray-700 dark:text-slate-300 text-sm mt-2 font-medium">Important:</p>
        <p className="text-gray-700 dark:text-slate-300 text-sm">MARS&#8482; is a tool and technology platform only. We do not:</p>
        <div className="text-gray-700 dark:text-slate-300 text-sm space-y-1 ml-3">
          <p>&#8226; Guarantee affiliate acquisition</p>
          <p>&#8226; Guarantee revenue, traffic, or performance outcomes</p>
          <p>&#8226; Control or endorse any third-party affiliates</p>
        </div>
      </div>

      <div>
        <h3 className="font-semibold text-gray-900 dark:text-slate-100 mb-2">2. Eligibility</h3>
        <p className="text-gray-700 dark:text-slate-300 text-sm">You must:</p>
        <div className="text-gray-700 dark:text-slate-300 text-sm space-y-1 ml-3">
          <p>&#8226; Be at least 18 years old</p>
          <p>&#8226; Use the Service on behalf of a legally registered business</p>
          <p>&#8226; Operate in compliance with all applicable laws and regulations</p>
        </div>
      </div>

      <div>
        <h3 className="font-semibold text-gray-900 dark:text-slate-100 mb-2">3. Account & Access</h3>
        <p className="text-gray-700 dark:text-slate-300 text-sm mb-1">You are responsible for:</p>
        <div className="text-gray-700 dark:text-slate-300 text-sm space-y-1 ml-3">
          <p>&#8226; Maintaining confidentiality of login credentials</p>
          <p>&#8226; All activities conducted through your account</p>
        </div>
        <p className="text-gray-700 dark:text-slate-300 text-sm mt-2">We reserve the right to suspend or terminate accounts for misuse or breach of these Terms.</p>
      </div>

      <div>
        <h3 className="font-semibold text-gray-900 dark:text-slate-100 mb-2">4. Fees & Payment</h3>
        <div className="text-gray-700 dark:text-slate-300 text-sm space-y-1">
          <p>&#8226; Fees are charged as per the agreed commercial terms</p>
          <p>&#8226; All fees are non-refundable unless otherwise agreed in writing</p>
          <p>&#8226; Late payments may result in suspension of Service or additional charges</p>
          <p>&#8226; We reserve the right to suspend access for non-payment</p>
        </div>
      </div>

      <div>
        <h3 className="font-semibold text-gray-900 dark:text-slate-100 mb-2">5. Term & Termination</h3>
        <div className="text-gray-700 dark:text-slate-300 text-sm space-y-1">
          <p>&#8226; Minimum term (if applicable) shall be as per commercial agreement</p>
          <p>&#8226; Either party may terminate with 30-90 days written notice after the minimum term</p>
          <p>&#8226; We may terminate immediately for breach of Terms, non-payment, or illegal use</p>
          <p>&#8226; No refunds will be issued upon termination</p>
        </div>
      </div>

      <div>
        <h3 className="font-semibold text-gray-900 dark:text-slate-100 mb-2">6. Acceptable Use</h3>
        <p className="text-gray-700 dark:text-slate-300 text-sm mb-1">You agree NOT to:</p>
        <div className="text-gray-700 dark:text-slate-300 text-sm space-y-1 ml-3">
          <p>&#8226; Use MARS&#8482; for illegal activities or in restricted jurisdictions</p>
          <p>&#8226; Engage in spam, misleading, or unethical affiliate practices</p>
          <p>&#8226; Violate any applicable gambling or advertising regulations</p>
          <p>&#8226; Reverse engineer or misuse the platform</p>
        </div>
      </div>

      <div>
        <h3 className="font-semibold text-gray-900 dark:text-slate-100 mb-2">7. Data Protection & Privacy</h3>
        <div className="text-gray-700 dark:text-slate-300 text-sm space-y-1">
          <p>&#8226; You retain ownership of your data</p>
          <p>&#8226; We act as a data processor for client-provided data</p>
          <p>&#8226; We may process data to deliver the Service and use aggregated and anonymised data to improve our platform</p>
        </div>
        <p className="text-gray-700 dark:text-slate-300 text-sm mt-2">You are responsible for ensuring lawful collection and use of data, and compliance with applicable laws (including GDPR where applicable).</p>
      </div>

      <div>
        <h3 className="font-semibold text-gray-900 dark:text-slate-100 mb-2">8. AI & Data Usage</h3>
        <p className="text-gray-700 dark:text-slate-300 text-sm mb-1">MARS&#8482; uses AI and automation technologies. You acknowledge that:</p>
        <div className="text-gray-700 dark:text-slate-300 text-sm space-y-1 ml-3">
          <p>&#8226; Outputs are generated algorithmically and may not always be accurate</p>
          <p>&#8226; Results are indicative and not guaranteed</p>
          <p>&#8226; The Company is not liable for decisions made based on outputs</p>
        </div>
      </div>

      <div>
        <h3 className="font-semibold text-gray-900 dark:text-slate-100 mb-2">9. Third-Party Dependencies</h3>
        <p className="text-gray-700 dark:text-slate-300 text-sm">MARS&#8482; may rely on external data sources and third-party platforms. We do not guarantee accuracy of third-party data or availability of external services. We are not liable for third-party failures.</p>
      </div>

      <div>
        <h3 className="font-semibold text-gray-900 dark:text-slate-100 mb-2">10. Intellectual Property</h3>
        <p className="text-gray-700 dark:text-slate-300 text-sm mb-1">All rights in MARS&#8482; — including software, algorithms, models, and platform design — remain the property of the Company.</p>
        <p className="text-gray-700 dark:text-slate-300 text-sm">You are granted a limited, non-exclusive, non-transferable license to use the Service. You may not copy, modify, resell, or reverse engineer any part of the platform.</p>
      </div>

      <div>
        <h3 className="font-semibold text-gray-900 dark:text-slate-100 mb-2">11. Outputs & Results</h3>
        <p className="text-gray-700 dark:text-slate-300 text-sm">Any outputs generated by MARS&#8482; are provided for your internal business use and do not grant ownership of underlying technology. We retain ownership of AI models, data structures, and methodologies.</p>
      </div>

      <div>
        <h3 className="font-semibold text-gray-900 dark:text-slate-100 mb-2">12. Service Availability</h3>
        <p className="text-gray-700 dark:text-slate-300 text-sm">We aim to provide reliable service but do not guarantee uninterrupted access. We may perform maintenance or update features. The Service is provided on a "commercially reasonable efforts" basis.</p>
      </div>

      <div>
        <h3 className="font-semibold text-gray-900 dark:text-slate-100 mb-2">13. Disclaimer of Warranties</h3>
        <p className="text-gray-700 dark:text-slate-300 text-sm">The Service is provided "as is" and "as available". We disclaim all warranties, including fitness for a particular purpose, accuracy of results, and continuous availability.</p>
      </div>

      <div>
        <h3 className="font-semibold text-gray-900 dark:text-slate-100 mb-2">14. Limitation of Liability</h3>
        <p className="text-gray-700 dark:text-slate-300 text-sm mb-1">The Company shall not be liable for:</p>
        <div className="text-gray-700 dark:text-slate-300 text-sm space-y-1 ml-3">
          <p>&#8226; Indirect or consequential damages</p>
          <p>&#8226; Loss of revenue, profits, or business opportunities</p>
          <p>&#8226; Data loss or inaccuracies</p>
          <p>&#8226; Actions of third-party affiliates</p>
        </div>
        <p className="text-gray-700 dark:text-slate-300 text-sm mt-2">Total liability shall be limited to fees paid by you in the preceding 3 months.</p>
      </div>

      <div>
        <h3 className="font-semibold text-gray-900 dark:text-slate-100 mb-2">15. Indemnification</h3>
        <p className="text-gray-700 dark:text-slate-300 text-sm">You agree to indemnify and hold harmless the Company against any claims arising from your use of the Service, breach of these Terms, or violation of applicable laws or regulations.</p>
      </div>

      <div>
        <h3 className="font-semibold text-gray-900 dark:text-slate-100 mb-2">16. Compliance & iGaming Regulations</h3>
        <p className="text-gray-700 dark:text-slate-300 text-sm">You are solely responsible for compliance with all gambling laws and regulations, and ensuring affiliate activities meet regulatory standards. We do not provide legal or regulatory advice.</p>
      </div>

      <div>
        <h3 className="font-semibold text-gray-900 dark:text-slate-100 mb-2">17. Modifications</h3>
        <p className="text-gray-700 dark:text-slate-300 text-sm">We may update these Terms from time to time. Continued use of the Service constitutes acceptance of updated Terms.</p>
      </div>

      <div>
        <h3 className="font-semibold text-gray-900 dark:text-slate-100 mb-2">18. Governing Law & Jurisdiction</h3>
        <p className="text-gray-700 dark:text-slate-300 text-sm">These Terms shall be governed by and construed in accordance with the laws of England and Wales. Any disputes shall be subject to the exclusive jurisdiction of the courts of London, United Kingdom.</p>
      </div>

      <div>
        <h3 className="font-semibold text-gray-900 dark:text-slate-100 mb-2">19. Contact</h3>
        <div className="bg-gray-50 dark:bg-slate-800 p-3 rounded text-sm">
          <p className="text-gray-700 dark:text-slate-300"><strong>Email:</strong> mars@mediacle.com</p>
          <p className="text-gray-700 dark:text-slate-300"><strong>Company:</strong> Mediacle Limited</p>
        </div>
      </div>
    </div>
  );

  // Show verification message after successful signup
  if (signupSuccess) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-indigo-50 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950 flex items-center justify-center p-4 relative overflow-hidden">
        {/* Animated Background Elements */}
        {floatingElements.map((element) => (
          <div
            key={element.id}
            className="absolute rounded-full bg-gradient-to-r from-teal-200/10 to-blue-200/10 dark:from-teal-500/5 dark:to-blue-500/5 animate-pulse"
            style={{
              left: `${element.x}%`,
              top: `${element.y}%`,
              width: `${element.size}px`,
              height: `${element.size}px`,
              animationDelay: `${element.delay}s`,
              animationDuration: '6s'
            }}
          />
        ))}

        {/* Verification Message Card */}
        <div className="w-full max-w-sm relative z-10">
          <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-xl border border-gray-200 dark:border-slate-700 p-8 text-center">

            {/* Logo */}
            <div className="mb-6">
              <img
                src={isDark ? "/MARS-White.png" : "/MARS-Color.png"}
                alt="MARS Logo"
                className="h-10 mx-auto object-contain"
              />
            </div>

            {/* Success icon */}
            <div className="mx-auto w-14 h-14 bg-blue-50 dark:bg-blue-900/30 rounded-full flex items-center justify-center mb-4">
              <CheckCircle className="w-7 h-7 text-blue-600 dark:text-blue-400" />
            </div>

            <h2 className="text-lg font-semibold text-gray-900 dark:text-slate-100 mb-1">
              Account Created
            </h2>
            <p className="text-sm text-gray-500 dark:text-slate-400 mb-6">
              A verification email has been sent to
            </p>

            {/* Email display */}
            <div className="bg-gray-50 dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-lg px-4 py-3 mb-6">
              <div className="flex items-center gap-3">
                <Mail className="w-4 h-4 text-blue-600 dark:text-blue-400 flex-shrink-0" />
                <p className="text-sm font-medium text-gray-900 dark:text-slate-100 break-all text-left">{userEmail}</p>
              </div>
            </div>

            {/* Instructions */}
            <p className="text-xs text-gray-500 dark:text-slate-400 leading-relaxed mb-6">
              Click the link in the email to verify your account. Don't see it? Check your spam folder or contact <span className="text-blue-600 dark:text-blue-400">mars@mediacle.com</span>
            </p>

            {/* Actions */}
            <a
              href="/login"
              className="w-full inline-flex items-center justify-center gap-2 py-2.5 px-4 bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 text-white font-medium text-sm rounded-lg transition-colors"
            >
              Go to Login
              <ArrowRight className="w-4 h-4" />
            </a>

            <button
              onClick={handleBackToSignup}
              className="w-full mt-3 py-2 text-gray-500 dark:text-slate-400 hover:text-gray-700 dark:hover:text-slate-300 text-xs font-medium transition-colors"
            >
              Create Another Account
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-indigo-50 dark:from-slate-950 dark:via-slate-900 dark:to-slate-950 flex items-center justify-center p-4 relative overflow-hidden">
      {/* Animated Background Elements */}
      {floatingElements.map((element) => (
        <div
          key={element.id}
          className="absolute rounded-full bg-gradient-to-r from-teal-200/10 to-blue-200/10 dark:from-teal-500/5 dark:to-blue-500/5 animate-pulse"
          style={{
            left: `${element.x}%`,
            top: `${element.y}%`,
            width: `${element.size}px`,
            height: `${element.size}px`,
            animationDelay: `${element.delay}s`,
            animationDuration: '6s'
          }}
        />
      ))}

      {/* Main Container */}
      <div className="w-full max-w-md relative z-10">

        {/* Signup Card */}
        <div className="bg-white/95 dark:bg-slate-900/95 backdrop-blur-xl rounded-2xl shadow-xl p-6 transition-all duration-500 hover:shadow-2xl relative border border-gray-100 dark:border-slate-700">
          
          {/* Logo and Header */}
          <div className="text-center mb-6">
            <div className="inline-flex items-center justify-center mb-3">
              <div className="w-32 h-12">
                <img
                  src={isDark ? "/MARS-White.png" : "/MARS-Color.png"}
                  alt="MARS Logo"
                  className="w-full h-full object-contain"
                />
              </div>
            </div>

            <p className="text-sm text-gray-600 dark:text-slate-400">Join MARS and start your journey</p>
          </div>

          {/* Submit Error Display */}
          {submitError && (
            <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg">
              <div className="flex items-center gap-2">
                <AlertCircle className="w-4 h-4 text-red-600 dark:text-red-400 flex-shrink-0" />
                <p className="text-sm text-red-700 dark:text-red-400">{submitError}</p>
              </div>
            </div>
          )}

          {/* Progress Bar */}
          <div className="mb-6">
            <div className="flex items-center justify-between text-xs text-gray-600 dark:text-slate-400 mb-2">
              <span>Complete your profile</span>
              <span className="font-medium">{Math.round(formProgress)}%</span>
            </div>
            <div className="w-full bg-gray-200 dark:bg-slate-700 rounded-full h-1.5 overflow-hidden">
              <div 
                className={`h-1.5 rounded-full transition-all duration-700 ease-out ${
                  formProgress === 100 ? 'bg-gradient-to-r from-green-500 to-emerald-500' : 'bg-gradient-to-r from-teal-500 to-blue-500'
                }`}
                style={{ width: `${formProgress}%` }}
              />
            </div>
          </div>

          {/* Signup Form */}
          <div className="space-y-4">
            
            {/* First Name & Last Name Row */}
            <div className="grid grid-cols-2 gap-3">
              {/* First Name Field */}
              <div className="relative">
                <label className="block text-xs font-medium text-gray-700 dark:text-slate-300 mb-1.5">
                  First Name
                </label>
                <div className="relative">
                  <div className="absolute left-3 top-1/2 transform -translate-y-1/2 z-10">
                    {getFieldIcon('firstName')}
                  </div>
                  
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    onFocus={() => setFocusedField('firstName')}
                    onBlur={handleBlur}
                    placeholder="First name"
                    className={getInputClassName('firstName')}
                  />
                  
                  {isFieldValid('firstName') && (
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                      <CheckCircle className="w-4 h-4 text-green-600" />
                    </div>
                  )}
                  
                  {errors.firstName && (
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                      <AlertCircle className="w-4 h-4 text-red-600" />
                    </div>
                  )}
                </div>
                
                {errors.firstName && (
                  <p className="mt-1 text-xs text-red-500 flex items-center gap-1">
                    <AlertCircle className="h-3 w-3" />
                    {errors.firstName}
                  </p>
                )}
              </div>

              {/* Last Name Field */}
              <div className="relative">
                <label className="block text-xs font-medium text-gray-700 dark:text-slate-300 mb-1.5">
                  Last Name
                </label>
                <div className="relative">
                  <div className="absolute left-3 top-1/2 transform -translate-y-1/2 z-10">
                    {getFieldIcon('lastName')}
                  </div>
                  
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    onFocus={() => setFocusedField('lastName')}
                    onBlur={handleBlur}
                    placeholder="Last name"
                    className={getInputClassName('lastName')}
                  />
                  
                  {isFieldValid('lastName') && (
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                      <CheckCircle className="w-4 h-4 text-green-600" />
                    </div>
                  )}
                  
                  {errors.lastName && (
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                      <AlertCircle className="w-4 h-4 text-red-600" />
                    </div>
                  )}
                </div>
                
                {errors.lastName && (
                  <p className="mt-1 text-xs text-red-500 flex items-center gap-1">
                    <AlertCircle className="h-3 w-3" />
                    {errors.lastName}
                  </p>
                )}
              </div>
            </div>

            {/* Business Email Field */}
            <div className="relative">
              <label className="block text-xs font-medium text-gray-700 dark:text-slate-300 mb-1.5">
                Business Email
              </label>
              <div className="relative">
                <div className="absolute left-3 top-1/2 transform -translate-y-1/2 z-10">
                  {getFieldIcon('businessEmail')}
                </div>
                
                <input
                  type="email"
                  name="businessEmail"
                  value={formData.businessEmail}
                  onChange={handleInputChange}
                  onFocus={() => setFocusedField('businessEmail')}
                  onBlur={handleBlur}
                  placeholder="Enter your business email"
                  className={getInputClassName('businessEmail')}
                />
                
                {isFieldValid('businessEmail') && (
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                  </div>
                )}
                
                {errors.businessEmail && (
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    <AlertCircle className="w-4 h-4 text-red-600" />
                  </div>
                )}
              </div>
              
              {errors.businessEmail && (
                <p className="mt-1 text-xs text-red-500 flex items-center gap-1">
                  <AlertCircle className="h-3 w-3" />
                  {errors.businessEmail}
                </p>
              )}
            </div>

            {/* Company Field */}
            <div className="relative">
              <label className="block text-xs font-medium text-gray-700 dark:text-slate-300 mb-1.5">
                Company
              </label>
              <div className="relative">
                <div className="absolute left-3 top-1/2 transform -translate-y-1/2 z-10">
                  {getFieldIcon('company')}
                </div>
                
                <input
                  type="text"
                  name="company"
                  value={formData.company}
                  onChange={handleInputChange}
                  onFocus={() => setFocusedField('company')}
                  onBlur={handleBlur}
                  placeholder="Enter your company name"
                  className={getInputClassName('company')}
                />
                
                {isFieldValid('company') && (
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    <CheckCircle className="w-4 h-4 text-green-600" />
                  </div>
                )}
                
                {errors.company && (
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    <AlertCircle className="w-4 h-4 text-red-600" />
                  </div>
                )}
              </div>
              
              {errors.company && (
                <p className="mt-1 text-xs text-red-500 flex items-center gap-1">
                  <AlertCircle className="h-3 w-3" />
                  {errors.company}
                </p>
              )}
            </div>

            {/* Password Field */}
            <div className="relative">
              <label className="block text-xs font-medium text-gray-700 dark:text-slate-300 mb-1.5">
                Password
              </label>
              <div className="relative">
                <div className="absolute left-3 top-1/2 transform -translate-y-1/2 z-10">
                  {getFieldIcon('password')}
                </div>
                
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  onFocus={() => setFocusedField('password')}
                  onBlur={handleBlur}
                  placeholder="Create a strong password"
                  className={getInputClassName('password')}
                />
                
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2 flex items-center gap-2">
                  {isFieldValid('password') && !showPassword && (
                    <CheckCircle className="w-4 h-4 text-green-600" />
                  )}
                  
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="flex items-center justify-center w-6 h-6 rounded hover:bg-gray-100 dark:hover:bg-slate-700 transition-all duration-200"
                  >
                    {showPassword ? (
                      <EyeOff className="w-4 h-4 text-gray-500 dark:text-slate-400" />
                    ) : (
                      <Eye className="w-4 h-4 text-gray-500 dark:text-slate-400" />
                    )}
                  </button>
                </div>
              </div>
              
              {/* Password Requirements */}
              {formData.password && (
                <div className="mt-2 bg-gray-50 dark:bg-slate-800 rounded-lg p-3">
                  <div className="text-xs font-medium text-gray-700 dark:text-slate-300 mb-2">Password Requirements:</div>
                  <div className="grid grid-cols-2 gap-1 text-xs">
                    <div className={`flex items-center gap-1.5 ${
                      formData.password.length >= 8 ? 'text-green-600 dark:text-green-400' : 'text-gray-500 dark:text-slate-400'
                    }`}>
                      <div className={`w-1.5 h-1.5 rounded-full ${
                        formData.password.length >= 8 ? 'bg-green-500' : 'bg-gray-300 dark:bg-slate-600'
                      }`} />
                      8+ characters
                    </div>

                    <div className={`flex items-center gap-1.5 ${
                      /(?=.*[a-z])/.test(formData.password) ? 'text-green-600 dark:text-green-400' : 'text-gray-500 dark:text-slate-400'
                    }`}>
                      <div className={`w-1.5 h-1.5 rounded-full ${
                        /(?=.*[a-z])/.test(formData.password) ? 'bg-green-500' : 'bg-gray-300 dark:bg-slate-600'
                      }`} />
                      Lowercase
                    </div>

                    <div className={`flex items-center gap-1.5 ${
                      /(?=.*[A-Z])/.test(formData.password) ? 'text-green-600 dark:text-green-400' : 'text-gray-500 dark:text-slate-400'
                    }`}>
                      <div className={`w-1.5 h-1.5 rounded-full ${
                        /(?=.*[A-Z])/.test(formData.password) ? 'bg-green-500' : 'bg-gray-300 dark:bg-slate-600'
                      }`} />
                      Uppercase
                    </div>

                    <div className={`flex items-center gap-1.5 ${
                      /(?=.*\d)/.test(formData.password) ? 'text-green-600 dark:text-green-400' : 'text-gray-500 dark:text-slate-400'
                    }`}>
                      <div className={`w-1.5 h-1.5 rounded-full ${
                        /(?=.*\d)/.test(formData.password) ? 'bg-green-500' : 'bg-gray-300 dark:bg-slate-600'
                      }`} />
                      Number
                    </div>

                    <div className={`flex items-center gap-1.5 col-span-2 ${
                      /(?=.*[!@#$%^&*(),.?":{}|<>])/.test(formData.password) ? 'text-green-600 dark:text-green-400' : 'text-gray-500 dark:text-slate-400'
                    }`}>
                      <div className={`w-1.5 h-1.5 rounded-full ${
                        /(?=.*[!@#$%^&*(),.?":{}|<>])/.test(formData.password) ? 'bg-green-500' : 'bg-gray-300 dark:bg-slate-600'
                      }`} />
                      Special character (!@#$%^&*)
                    </div>
                  </div>
                </div>
              )}
              
              {errors.password && (
                <p className="mt-1 text-xs text-red-500 flex items-center gap-1">
                  <AlertCircle className="h-3 w-3" />
                  {errors.password}
                </p>
              )}
            </div>

            {/* Terms and Robot Checker */}
            <div className="space-y-3 pt-2">
              {/* Accept Terms */}
              <div className="relative">
                <label className="flex items-start gap-2.5 cursor-pointer">
                  <div className="relative mt-0.5">
                    <input
                      type="checkbox"
                      name="acceptTerms"
                      checked={formData.acceptTerms}
                      onChange={handleInputChange}
                      className="sr-only"
                    />
                    <div className={`w-4 h-4 border-2 rounded transition-all duration-300 flex items-center justify-center ${
                      formData.acceptTerms
                        ? 'bg-teal-500 border-teal-500'
                        : errors.acceptTerms
                        ? 'border-red-400'
                        : 'border-gray-300 dark:border-slate-600'
                    }`}>
                      {formData.acceptTerms && (
                        <CheckCircle className="w-2.5 h-2.5 text-white" />
                      )}
                    </div>
                  </div>
                  <span className="text-xs text-gray-600 dark:text-slate-400 leading-relaxed">
                    I accept the{' '}
                    <button
                      type="button"
                      onClick={() => setShowTermsModal(true)}
                      className="text-teal-600 dark:text-teal-400 hover:text-teal-700 dark:hover:text-teal-300 font-medium hover:underline"
                    >
                      Terms of Service
                    </button>{' '}
                    and{' '}
                    <button
                      type="button"
                      onClick={() => setShowPrivacyModal(true)}
                      className="text-teal-600 dark:text-teal-400 hover:text-teal-700 dark:hover:text-teal-300 font-medium hover:underline"
                    >
                      Privacy Policy
                    </button>
                  </span>
                </label>
                {errors.acceptTerms && (
                  <p className="mt-1 text-xs text-red-500 flex items-center gap-1">
                    <AlertCircle className="h-3 w-3" />
                    {errors.acceptTerms}
                  </p>
                )}
              </div>

              {/* Google reCAPTCHA v2 */}
              <div className="relative">
                <div ref={recaptchaRef} className="flex justify-center"></div>
                {errors.recaptcha && (
                  <p className="mt-1 text-xs text-red-500 flex items-center gap-1">
                    <AlertCircle className="h-3 w-3" />
                    {errors.recaptcha}
                  </p>
                )}
              </div>
            </div>

            {/* Create Account Button */}
            <button
              onClick={handleSubmit}
              disabled={!isFormValid || isLoading}
              className={`w-full py-3 px-4 rounded-lg font-medium text-sm text-white transition-all duration-500 relative overflow-hidden ${
                !isFormValid || isLoading
                  ? 'bg-gray-400 dark:bg-slate-600 cursor-not-allowed'
                  : 'bg-gradient-to-r from-teal-500 to-teal-600 hover:from-teal-600 hover:to-teal-700 shadow-md hover:shadow-lg'
              }`}
            >
              <div className="relative flex items-center justify-center gap-2 h-5">
                {/* Loading state */}
                <div className={`absolute inset-0 flex items-center justify-center gap-2 transition-all duration-500 ${
                  isLoading ? 'opacity-100' : 'opacity-0'
                }`}>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  <span>Creating Account...</span>
                </div>

                {/* Default state */}
                <div className={`absolute inset-0 flex items-center justify-center gap-2 transition-all duration-500 ${
                  !isLoading ? 'opacity-100' : 'opacity-0'
                }`}>
                  <span>Create Account</span>
                  <ArrowRight className="h-4 w-4" />
                </div>
              </div>
            </button>

            {/* Google Sign-Up — uncomment to enable
            <div className="relative my-4">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-200 dark:border-slate-700"></div>
              </div>
              <div className="relative flex justify-center text-xs">
                <span className="px-3 bg-white dark:bg-slate-900 text-gray-400 dark:text-slate-500">or sign up with</span>
              </div>
            </div>
            <div className="flex justify-center">
              <GoogleLogin
                onSuccess={handleGoogleSuccess}
                onError={() => setSubmitError('Google signup failed. Please try again.')}
                theme={isDark ? 'filled_black' : 'outline'}
                size="large"
                width="100%"
                text="signup_with"
                shape="rectangular"
              />
            </div>
            */}
          </div>

          {/* Footer */}
          <div className="mt-6 text-center border-t border-gray-100 dark:border-slate-700 pt-4">
            <p className="text-xs text-gray-600 dark:text-slate-400">
              Already have an account?{' '}
              <a
                href="/login"
                className="text-teal-600 dark:text-teal-400 hover:text-teal-700 dark:hover:text-teal-300 font-medium transition-all duration-300 hover:underline transform hover:scale-105"
              >
                Sign in here
              </a>
            </p>
          </div>
        </div>
      </div>

      {/* Privacy Policy Modal */}
      <Modal 
        isOpen={showPrivacyModal} 
        onClose={() => setShowPrivacyModal(false)}
        title="Privacy Policy"
      >
        <PrivacyPolicyContent />
      </Modal>

      {/* Terms of Service Modal */}
      <Modal 
        isOpen={showTermsModal} 
        onClose={() => setShowTermsModal(false)}
        title="Terms of Service"
      >
        <TermsOfServiceContent />
      </Modal>
    </div>
  );
};

export default MarsSignupForm;